// Frontend API client for the follow-derived bands of `/whats-on`.
//
// The API namespace stays `/api/following` on purpose: it serves the bands
// derived from who you follow. The PAGE is `/whats-on` because it also carries
// bands that have nothing to do with following.
//
// One endpoint (`/bands`) serves both follow-derived bands together — the
// ranked bands below them come from the ordinary feed endpoint.
//
// Authed only — the whole payload is derived from the caller's follow list.

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { getLocalDateString } from '../utils/dateUtils';

const API = `${API_BASE}/api/following`;

// An empty result and a FAILED result are not the same thing here: this page
// is a notification target with a completeness contract, so "we found nothing
// new" must never be shown when the truth is "the request failed". Callers get
// `error` and render a retry instead of the empty state.
const EMPTY = {
  newly: { groups: [], itemCount: 0, lastAlertAt: null, newSinceLastAlertCount: 0, truncated: false },
  upcoming: { items: [], truncated: false },
  home: null,
  tripLegs: [],
  nearbyRadiusKm: null
};

class FollowingService {
  /**
   * Both follow-derived bands in one call. They arrive together so `upcoming`
   * can exclude exactly what `newly` rendered — two requests could disagree if
   * a sync landed between them, and the same card would show up twice.
   *
   * @param {object} [opts]
   * @param {number} [opts.days]  override the "new" window (QA / stale dev data)
   * @param {number} [opts.limit] cap items per band — the Home teaser asks for
   *                              a handful, not the page's full bands
   */
  static async getBands({ days, limit, tripAware } = {}) {
    try {
      const params = new URLSearchParams();
      if (days) params.set('days', days);
      if (limit) params.set('limit', limit);
      // Only surfaces that RENDER the trip labels ask for the trip-widened
      // bands — see the route comment. The Home teaser deliberately does not.
      if (tripAware) params.set('tripAware', '1');
      // The viewer's own date is the server's "still on" floor. Without it the
      // server keeps anything still on SOMEWHERE (a 12h floor), and inside a
      // run that cannot be trimmed here: last night's show east of UTC stayed
      // the run's next date and was counted (server/utils/eventDayFloor.js).
      params.set('today', getLocalDateString(new Date()));
      // .toString(), never .size — URLSearchParams.size is Safari 17+ / Chrome
      // 113+ and this build ships no polyfill, so on iOS 16 `params.size > 0`
      // is `undefined > 0` = false and the whole query string silently
      // vanishes: the teaser's ?limit= (and the ?days= QA override) drop, and
      // the server returns the full page bands to exactly the devices least
      // able to afford them.
      const qsBody = params.toString();
      const qs = qsBody ? `?${qsBody}` : '';
      const res = await fetch(`${API}/bands${qs}`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await AuthService.handleResponse(res);
      return {
        newly: {
          groups: data.newly?.groups || [],
          itemCount: data.newly?.itemCount || 0,
          lastAlertAt: data.newly?.lastAlertAt || null,
          newSinceLastAlertCount: data.newly?.newSinceLastAlertCount || 0,
          truncated: !!data.newly?.truncated
        },
        upcoming: {
          items: data.upcoming?.items || [],
          truncated: !!data.upcoming?.truncated
        },
        // Centre, country and radius for the RANKED bands. Comes from the
        // server because the browser only offers coordinates when the user
        // opted into location sharing, and "near you" has to filter either
        // way; the country scopes which holidays the book-ahead band knows.
        home: data.home || null,
        // Active trip legs for the page's trip labels. This shape is an
        // explicit whitelist, so a new server field is invisible until it is
        // named here — the same way `convertToActivity` once swallowed new API
        // fields silently. Add the field in BOTH places or the page sees null.
        tripLegs: data.tripLegs || [],
        nearbyRadiusKm: data.nearbyRadiusKm || null
      };
    } catch (err) {
      console.error('FollowingService.getBands:', err);
      return { ...EMPTY, error: err?.message || true };
    }
  }
}

export default FollowingService;
